import React, { useState, useEffect, useMemo } from 'react';
import { isEmpty } from 'lodash';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import SystemMasterTabel from '../../Components/SystemMasterTabel/SystemMasterTabel';
import * as action from './Store/SystemMasters.action';
import ConfirmationPopup from '../../Components/ModalPopup/ConfirmationPopup';
import { useComponent } from '../UserSetting/UsersLibrary/UserRights';

const tempObj = {
  name: null,
  isEmpty: false,
  isNew: true,
  isActive: true,
  hasLength: false,
  _id: '',
};

/**
 * Modules that store a Leg Length reference, in the order they are named to the admin.
 * `key` matches a flag on the in-use-status API's `modules` object.
 */
const LENGTH_CONSUMER_MODULES = [
  { key: 'developmentOrder', labelKey: 'SYSTEM_MASTER_TABLE.LENGTH_MODULE_DEVELOPMENT_ORDER' },
  { key: 'productIdentifier', labelKey: 'SYSTEM_MASTER_TABLE.LENGTH_MODULE_PRODUCT_IDENTIFIER' },
  { key: 'productCosting', labelKey: 'SYSTEM_MASTER_TABLE.LENGTH_MODULE_PRODUCT_COSTING' },
  { key: 'samples', labelKey: 'SYSTEM_MASTER_TABLE.LENGTH_MODULE_SAMPLES' },
  { key: 'purchaseOrder', labelKey: 'SYSTEM_MASTER_TABLE.LENGTH_MODULE_PURCHASE_ORDER' },
];

function LengthMaster() {
  const legLengths = 'leg-lengths';
  const dispatch = useDispatch();
  const reducer = useSelector((estate) => estate);
  const {
    SystemMastersDetails,
    UserDetails,
    Users: { logInRights },
  } = reducer;
  const { lengthsList, lengthVisible, lengthInUse, lengthUsageModules,variableOneName } = SystemMastersDetails;
  const { userInfo } = UserDetails;
  const { t } = useTranslation('common');
  const { t: tSystemMaster } = useTranslation('systemMaster');
  const loggedInUserId = userInfo.userId;

  const [isLengthVisible, setIsLengthVisible] = useState(false);
  const [variableOneNameValue, setVariableOneNameValue] = useState('');
  const [isSavingName, setIsSavingName] = useState(false);
  const [openPopup, setOpenPopup] = useState(false);
  const [isLoadingLength, setIsLoadingLength] = useState(false);
  const [showLengths, setShowLengths] = useState(false);
  const [IsLenUnique, setIsLenUnique] = useState(true);
  const [buttonDownLengths, setButtonDownLengths] = useState(true);
  const [lenBtnDisable, setLenBtnDisable] = useState(false);
  const [addingLengths, setAddingLengths] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showConfirmationAD, setShowConfirmationAD] = useState(false);
  const [isLoadingConf, setIsLoadingConf] = useState(false);
  const [isLength, setIsLength] = useState(false);
  const [lengthsData, setLengthsData] = useState([]);
  const [isUniqLenId, setIsUniqLenId] = useState([]);
  const [deleteId, setDeleteId] = useState('');
  const [activeInactiveID, setActiveInactiveID] = useState('');
  const { CheckActionPermission } = useComponent();
  const [userRights, setUserRights] = useState({});
  useEffect(() => {
    if (logInRights) {
      setUserRights(logInRights);
    }
  }, [logInRights]);
  const MasterAddEditAccess = CheckActionPermission(userRights, 'masters', 'length', 'addEdit');
  const MasterDeleteAccess = CheckActionPermission(userRights, 'masters', 'length', 'addEdit');

  useEffect(() => {
    if (lengthVisible !== undefined) {
      setIsLengthVisible(lengthVisible);
    }
  }, [lengthVisible]);

  useEffect(() => {
    if (variableOneName !== undefined && variableOneName !== null) {
      setVariableOneNameValue(variableOneName);
    }
  }, [variableOneName]);

  useEffect(() => {
    setLengthsData(lengthsList);
    if (addingLengths) {
      setLengthsData([
        ...lengthsList,
        {
          name: null,
          isEmpty: false,
          isNew: true,
          isActive: true,
          _id: '',
        },
      ]);
    }
  }, [lengthsList, addingLengths]);

  const callApi = async () => {
    setButtonDownLengths(!buttonDownLengths);
    setIsLoadingLength(true);
    buttonDownLengths ? setShowLengths(true) : setShowLengths(false);
    const lParams = {
      loggedInUserId,
    };
    isEmpty(lengthsData) && (await dispatch(action.getLengthsDetails(lParams)));
    setIsLoadingLength(false);
  };

  useEffect(() => {
    const getApi = async () => {
      await dispatch(action.getLengthAccess());
      await dispatch(action.getLengthInUseStatus());
    };
    getApi();
  }, [dispatch]);

  /**
   * The toggle is locked only against being switched OFF: once a module stores a length, hiding
   * lengths would hide data that already exists. With nothing using lengths the admin is free to
   * switch it either way.
   */
  const isLengthToggleLocked = Boolean(lengthInUse) && isLengthVisible;

  /**
   * The tenant-renameable Variable 1 label shown in Length master toasts. Prefers the live input
   * value, falls back to the saved name, then to the default "Lengths" so a toast is never blank.
   */
  const effectiveLengthName = variableOneNameValue?.trim() || variableOneName?.trim() || 'Lengths';

  const lengthUsageTooltip = useMemo(() => {
    if (!isLengthToggleLocked) {
      return '';
    }
    const usedIn = LENGTH_CONSUMER_MODULES.filter((module) => lengthUsageModules?.[module.key]).map((module) => tSystemMaster(module.labelKey));
    if (isEmpty(usedIn)) {
      return '';
    }
    return tSystemMaster('SYSTEM_MASTER_TABLE.LENGTH_IN_USE_TOOLTIP', { modules: usedIn.join(', ') });
  }, [isLengthToggleLocked, lengthUsageModules, tSystemMaster]);

  const handleChanged = (event, i) => {
    const lengths = [...lengthsData];
    setIsLenUnique(true);
    setIsUniqLenId([]);
    lengths[i] = {
      ...lengths[i],
      name: event.target.value,
      isEmpty: event.target.value === '',
    };
    const arr = [];
    const arrIndex = [];
    lengths.forEach((ele, ind) => {
      lengths.forEach((objs, index) => {
        if (ele.name === objs.name && ind !== index) {
          arr.push(ele);
          arrIndex.push(index);
        }
      });
    });
    if (!isEmpty(arr)) {
      const arrs = [...arrIndex];
      const removeArss = Math.min(...arrs);
      const newArr = arrIndex.filter((ele) => ele !== removeArss);
      setIsUniqLenId([...newArr]);
    }
    setIsLenUnique(isEmpty(arr));
    let isDisableLength = false;
    lengths.forEach((lensElement) => {
      if (isEmpty(lensElement?.name?.trim())) {
        isDisableLength = true;
      }
    });
    setLenBtnDisable(isDisableLength);
    setLengthsData(lengths);
  };

  const addLen = async () => {
    if (!showLengths) {
      setAddingLengths(true);
      setButtonDownLengths(!buttonDownLengths);
      setIsLoadingLength(true);
      buttonDownLengths ? setShowLengths(true) : setShowLengths(false);
      const lParams = {
        loggedInUserId,
      };
      isEmpty(lengthsData) && (await dispatch(action.getLengthsDetails(lParams)));
      setIsLoadingLength(false);
    } else if (!isEmpty(lengthsData)) {
      setLengthsData([...lengthsData, tempObj]);
    } else {
      setLengthsData([tempObj]);
    }
  };

  const handleDelete = (id) => {
    if (typeof id === 'number' || id === '') {
      lengthsData.splice(id, 1);
      lengthsData.forEach((lengthsElement) => {
        if (isEmpty(lengthsElement?.name?.trim()) && lengthsElement.isNew) {
          setLenBtnDisable(true);
        } else {
          setLenBtnDisable(false);
        }
      });
      setIsLenUnique(true);
      setIsUniqLenId([]);
      setLengthsData([...lengthsData]);
    } else {
      setDeleteId(id);
      setShowConfirmation(true);
    }
  };

  const saveLen = async () => {
    setIsLength(true);
    if (!isEmpty(lengthsData)) {
      const emptyField = await lengthsData.filter((lnitem) => lnitem.name === null || lnitem.name === '');

      if (isEmpty(emptyField)) {
        const data = lengthsData.map((lenobj) => {
          return { ...lenobj, name: lenobj?.name?.trim() };
        });
        const lengthSaveParams = {
          loggedInUserId,
          names: JSON.stringify(data),
          title: legLengths,
          masterName: effectiveLengthName,
        };
        setAddingLengths(false);
        await dispatch(action.saveToSystemMaster(lengthSaveParams, t));
        const lParams = {
          loggedInUserId,
        };
        await dispatch(action.getLengthsDetails(lParams));
        // post API
      } else {
        const newState = lengthsData.map((lnobj) => (lnobj?.isEmpty === false && lnobj.name === null ? { ...lnobj, isEmpty: true } : lnobj));
        setLengthsData(newState);
      }
    }
    setIsLength(false);
  };

  const activeInactiveClicked = (id) => {
    setShowConfirmationAD(true);
    setActiveInactiveID(id);
  };

  useEffect(() => {
    const array = [];
    lengthsData.forEach((ele, index) => {
      lengthsData.forEach((obj, places) => {
        if (index !== places && ele?.name === obj?.name) {
          array.push(ele);
        }
      });
    });
    if (isEmpty(array)) {
      setIsLenUnique(isEmpty(array));
      setIsUniqLenId([]);
    }
  }, [lengthsData]);

  const deleteConfirmed = async () => {
    setIsLoadingConf(true);
    const lengthsParams = {
      loggedInUserId,
      legLengthId: deleteId,
      title: legLengths,
      masterName: effectiveLengthName,
    };
    await dispatch(action.deleteFromSystemMasters(lengthsParams, t));
    setIsLoadingConf(false);
    setShowConfirmation(false);
    setLengthsData([...lengthsList]);
    const lParams = {
      loggedInUserId,
    };
    await dispatch(action.getLengthsDetails(lParams));
  };

  const activeInactiveConfirmed = async () => {
    setIsLoadingConf(true);
    let len = {};
    lengthsData.forEach((ele) => {
      if (ele._id === activeInactiveID) {
        ele.isActive = !ele.isActive;
        const data = {
          ...ele,
          loggedInUserId,
          title: legLengths,
          masterName: effectiveLengthName,
        };
        len = data;
      }
    });
    await dispatch(action.setActiveInactive(len, t));
    setIsLoadingConf(false);
    setShowConfirmationAD(false);
    setLengthsData([...lengthsData]);
  };

  const handleCheckboxChangeLength = () => {
    if (isLengthToggleLocked) {
      return;
    }
    setOpenPopup(!openPopup);
  };

  const toggleConfirmation = async () => {
    setIsLengthVisible(!isLengthVisible);
    setOpenPopup(false);
    await dispatch(action.saveLengthAccess({ lengthVisible: !isLengthVisible, masterName: effectiveLengthName }, t));
    const loggedInData = localStorage.getItem('loggedInUserInfo');
    const loggedInDataObj = { ...JSON.parse(loggedInData), lengthVisible: !isLengthVisible };
    localStorage.setItem('loggedInUserInfo', JSON.stringify(loggedInDataObj));
  };

  const handleVariableOneNameChange = (event) => {
    setVariableOneNameValue(event.target.value);
  };

  const saveVariableOneName = async () => {
    const trimmedName = variableOneNameValue?.trim();
    if (isEmpty(trimmedName) || trimmedName === variableOneName) {
      return;
    }
    setIsSavingName(true);
    await dispatch(action.saveLengthAccess({ variableOneName: trimmedName }, t));
    await dispatch(action.getLengthAccess());
    const loggedInData = localStorage.getItem('loggedInUserInfo');
    const loggedInDataObj = { ...JSON.parse(loggedInData), variableOneName: trimmedName };
    localStorage.setItem('loggedInUserInfo', JSON.stringify(loggedInDataObj));
    setIsSavingName(false);
  };

  return (
    <>
      <SystemMasterTabel
        isLoading={isLoadingLength}
        setShow={showLengths}
        callApi={callApi}
        buttonDown={buttonDownLengths}
        mainData={lengthsData}
        handleChanged={handleChanged}
        addBtnClicked={addLen}
        deletPressed={handleDelete}
        onSave={saveLen}
        isDesabled={isLength}
        title={t('LENGTH_MASTER.TITLE')}
        masterAddEditAccess={MasterAddEditAccess}
        masterDeleteAccess={MasterDeleteAccess}
        inputName="lengths"
        activeInactiveClicked={activeInactiveClicked}
        btnDisable={lenBtnDisable}
        isUnique={IsLenUnique}
        isUniqueId={isUniqLenId}
        handleCheckboxChangeLength={handleCheckboxChangeLength}
        userRole={userInfo?.role}
        isLengthVisible={isLengthVisible}
        isLengthToggleLocked={isLengthToggleLocked}
        lengthToggleLockedReason={lengthUsageTooltip}
        editableName={variableOneNameValue}
        onEditableNameChange={handleVariableOneNameChange}
        onEditableNameSave={saveVariableOneName}
        isSavingName={isSavingName}
      />
      {showConfirmation && (
        <ConfirmationPopup
          onOpen={showConfirmation}
          onConfirm={() => deleteConfirmed()}
          title={t('LENGTH_MASTER.DELETE_TITLE')}
          message={t('LENGTH_MASTER.DELETE_MESSAGE')}
          toggle={() => {
            setShowConfirmation(!showConfirmation);
          }}
          loading={isLoadingConf}
        />
      )}
      {openPopup && (
        <ConfirmationPopup
          onOpen={openPopup}
          toggle={() => setOpenPopup(false)}
          title={isLengthVisible ? t('SYSTEM_MASTER_TABLE.CONFIRMATION') : t('SYSTEM_MASTER_TABLE.CONFIRMATION_TITLE')}
          message={isLengthVisible ? t('SYSTEM_MASTER_TABLE.CONFIRMATION_MESSAGE') : t('SYSTEM_MASTER_TABLE.CONFIRMATION_MESSAGE_TITLE')}
          onConfirm={toggleConfirmation}
        />
      )}
      {showConfirmationAD && (
        <ConfirmationPopup
          onOpen={showConfirmationAD}
          onConfirm={() => activeInactiveConfirmed()}
          title={t('LENGTH_MASTER.ACTIVATE_DEACTIVATE_TITLE')}
          message={t('LENGTH_MASTER.ACTIVATE_DEACTIVATE_MESSAGE')}
          toggle={() => {
            setShowConfirmationAD(!showConfirmationAD);
          }}
          loading={isLoadingConf}
        />
      )}
    </>
  );
}
export default LengthMaster;
